import { supabase } from '../lib/supabase';

/**
 * Student Analytics Service
 * Fetches comprehensive learning analytics data for students
 */
export const studentAnalyticsService = {
  /**
   * Get progress velocity - XP earned over time periods
   * @param {string} userId - The student's user ID
   * @param {number} days - Number of days to fetch (default 30)
   */
  async getProgressVelocity(userId, days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase?.from('user_xp_events')?.select('created_at, amount, source, lesson_section')?.eq('user_id', userId)?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: true });

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(event => ({
        createdAt: event?.created_at,
        amount: event?.amount,
        source: event?.source,
        lessonSection: event?.lesson_section
      })) || [];
    } catch (error) {
      console.error('Error fetching progress velocity:', error);
      throw error;
    }
  },

  /**
   * Get content mastery breakdown by musicals and songs
   * @param {string} userId - The student's user ID
   */
  async getContentMastery(userId) {
    try {
      // Fetch lesson progress with song and musical details
      const { data, error } = await supabase?.from('user_lesson_progress')?.select(`
          lesson_section,
          lesson_status,
          quiz_completed,
          quiz_score,
          is_quiz_perfect,
          completed_at,
          song_id,
          songs (
            title,
            musical_id,
            musicals (
              title
            )
          )
        `)?.eq('user_id', userId);

      if (error) throw error;

      // Convert to camelCase and aggregate by content type
      const lessons = data?.map(lesson => ({
        lessonSection: lesson?.lesson_section,
        lessonStatus: lesson?.lesson_status,
        quizCompleted: lesson?.quiz_completed,
        quizScore: lesson?.quiz_score,
        isQuizPerfect: lesson?.is_quiz_perfect,
        completedAt: lesson?.completed_at,
        songId: lesson?.song_id,
        songTitle: lesson?.songs?.title,
        musicalId: lesson?.songs?.musical_id,
        musicalTitle: lesson?.songs?.musicals?.title
      })) || [];

      return lessons;
    } catch (error) {
      console.error('Error fetching content mastery:', error);
      throw error;
    }
  },

  /**
   * Get strengths analysis - quiz performance and completion rates
   * @param {string} userId - The student's user ID
   */
  async getStrengthsAnalysis(userId) {
    try {
      // Fetch quiz attempts with details
      const { data: quizData, error: quizError } = await supabase?.from('lesson_quiz_attempts')?.select(`score,is_perfect,created_at,quiz_id,lesson_quizzes (lesson_section,song_id,songs (title))`)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (quizError) throw quizError;

      // Fetch lesson progress for completion metrics
      const { data: progressData, error: progressError } = await supabase?.from('user_lesson_progress')?.select(`lesson_section,lesson_status,completed_at,times_repeated,video_progress_percentage`)?.eq('user_id', userId);

      if (progressError) throw progressError;

      // Convert to camelCase
      const quizAttempts = quizData?.map(quiz => ({
        score: quiz?.score,
        isPerfect: quiz?.is_perfect,
        createdAt: quiz?.created_at,
        lessonSection: quiz?.lesson_quizzes?.lesson_section,
        songTitle: quiz?.lesson_quizzes?.songs?.title
      })) || [];

      const lessonProgress = progressData?.map(lesson => ({
        lessonSection: lesson?.lesson_section,
        lessonStatus: lesson?.lesson_status,
        completedAt: lesson?.completed_at,
        timesRepeated: lesson?.times_repeated,
        videoProgressPercentage: parseFloat(lesson?.video_progress_percentage || 0)
      })) || [];

      return {
        quizAttempts,
        lessonProgress
      };
    } catch (error) {
      console.error('Error fetching strengths analysis:', error);
      throw error;
    }
  },

  /**
   * Get learning trends - streaks, levels, badges, and milestones
   * @param {string} userId - The student's user ID
   */
  async getLearningTrends(userId) {
    try {
      // Fetch user profile with streak and XP data
      const { data: profileData, error: profileError } = await supabase?.from('user_profiles')?.select('daily_streak, longest_streak, current_level, total_lifetime_xp, last_activity_date')?.eq('id', userId)?.single();

      if (profileError) throw profileError;

      // Fetch badges progress
      const { data: badgesData, error: badgesError } = await supabase?.from('user_badges')?.select(`
          status,
          progress,
          earned_at,
          badges (
            title,
            category,
            points
          )
        `)?.eq('user_id', userId);

      if (badgesError) throw badgesError;

      // Fetch XP history for trend analysis (last 90 days)
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo?.setDate(ninetyDaysAgo?.getDate() - 90);

      const { data: xpHistory, error: xpError } = await supabase?.from('user_xp_events')?.select('created_at, amount, source')?.eq('user_id', userId)?.gte('created_at', ninetyDaysAgo?.toISOString())?.order('created_at', { ascending: true });

      if (xpError) throw xpError;

      // Convert to camelCase
      return {
        profile: {
          dailyStreak: profileData?.daily_streak,
          longestStreak: profileData?.longest_streak,
          currentLevel: profileData?.current_level,
          totalLifetimeXp: profileData?.total_lifetime_xp,
          lastActivityDate: profileData?.last_activity_date
        },
        badges: badgesData?.map(badge => ({
          status: badge?.status,
          progress: parseFloat(badge?.progress || 0),
          earnedAt: badge?.earned_at,
          title: badge?.badges?.title,
          category: badge?.badges?.category,
          points: badge?.badges?.points
        })) || [],
        xpHistory: xpHistory?.map(event => ({
          createdAt: event?.created_at,
          amount: event?.amount,
          source: event?.source
        })) || []
      };
    } catch (error) {
      console.error('Error fetching learning trends:', error);
      throw error;
    }
  },

  /**
   * Get complete analytics dashboard data for a student
   * @param {string} userId - The student's user ID
   */
  async getCompleteAnalytics(userId) {
    try {
      const [velocity, mastery, strengths, trends] = await Promise.all([
        this.getProgressVelocity(userId),
        this.getContentMastery(userId),
        this.getStrengthsAnalysis(userId),
        this.getLearningTrends(userId)
      ]);

      return {
        progressVelocity: velocity,
        contentMastery: mastery,
        strengthsAnalysis: strengths,
        learningTrends: trends
      };
    } catch (error) {
      console.error('Error fetching complete analytics:', error);
      throw error;
    }
  }
};